import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import StarIcon from "@mui/icons-material/Star";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Card from "../../components/common/Card";
import MuiTable from "../../components/common/MuiTable";
import { Column } from "../../utils/constants";
import {
  ReviewModerationService,
  PendingReview,
} from "../../api/reviewModerationService";
import moment from "moment";

const ReviewModeration = () => {
  const { t } = useTranslation();
  const [dataTable, setDataTable] = useState<PendingReview[]>([]);
  const [isLoading, setLoading] = useState(false);
  const [totalElements, setTotalElements] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Reject modal state
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [selectedReviewId, setSelectedReviewId] = useState<number | null>(null);
  const [rejectionNote, setRejectionNote] = useState("");
  const [isSubmittingReject, setIsSubmittingReject] = useState(false);

  // Approve confirmation state
  const [isApprovingId, setIsApprovingId] = useState<number | null>(null);

  useEffect(() => {
    fetchPendingReviews();
  }, [page, rowsPerPage]);

  const fetchPendingReviews = async () => {
    try {
      setLoading(true);
      const response = await ReviewModerationService.getPendingReviews(
        page,
        rowsPerPage
      );

      // Access data via response.data (RestPagingResponse structure)
      // Structure: { success, code, data: [...], total, currentPage, perPage, ... }
      setDataTable(response.data.data || []);
      setTotalElements(response.data.total || 0);
    } catch (error: any) {
      console.error("Error fetching pending reviews:", error);
      toast.error(
        error.response?.data?.message ||
          t("review_moderation.load_error")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (reviewId: number) => {
    try {
      setIsApprovingId(reviewId);
      const response = await ReviewModerationService.approveReview(
        reviewId,
        t("review_moderation.approve_note")
      );

      const result = response.data;

      toast.success(result.message || t("review_moderation.approve_ok"));

      // Refresh the list
      await fetchPendingReviews();
    } catch (error: any) {
      console.error("Error approving review:", error);
      toast.error(
        error.response?.data?.message ||
          t("review_moderation.approve_error")
      );
    } finally {
      setIsApprovingId(null);
    }
  };

  const handleOpenRejectModal = (reviewId: number) => {
    setSelectedReviewId(reviewId);
    setRejectionNote("");
    setOpenRejectModal(true);
  };

  const handleCloseRejectModal = () => {
    setOpenRejectModal(false);
    setSelectedReviewId(null);
    setRejectionNote("");
  };

  const handleRejectSubmit = async () => {
    if (!rejectionNote.trim()) {
      toast.error(t("review_moderation.reason_required"));
      return;
    }

    if (!selectedReviewId) return;

    try {
      setIsSubmittingReject(true);
      const response = await ReviewModerationService.rejectReview(
        selectedReviewId,
        rejectionNote
      );

      const result = response.data;

      toast.success(result.message || t("review_moderation.reject_ok"));

      handleCloseRejectModal();

      // Refresh the list
      await fetchPendingReviews();
    } catch (error: any) {
      console.error("Error rejecting review:", error);
      toast.error(
        error.response?.data?.message ||
          t("review_moderation.reject_error")
      );
    } finally {
      setIsSubmittingReject(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderRatingStars = (score: number) => {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
        {[...Array(5)].map((_, index) => (
          <StarIcon
            key={index}
            sx={{
              fontSize: 16,
              color: index < score ? "#FFD700" : "#E0E0E0",
            }}
          />
        ))}
        <Typography variant="body2" sx={{ ml: 0.5 }}>
          ({score})
        </Typography>
      </Box>
    );
  };

  const header: Column[] = [
    {
      id: "id",
      label: t("review_moderation.col.id"),
      minWidth: 50,
      align: "center",
    },
    {
      id: "salon_name",
      label: t("review_moderation.col.salon"),
      minWidth: 150,
      align: "left",
    },
    {
      id: "reviewer_name",
      label: t("review_moderation.col.reviewer"),
      minWidth: 120,
      align: "left",
    },
    {
      id: "score_outlet",
      label: t("review_moderation.col.rating"),
      minWidth: 120,
      align: "center",
      format: (value: number) => renderRatingStars(value),
    },
    {
      id: "review_text",
      label: t("review_moderation.col.text"),
      minWidth: 250,
      align: "left",
      format: (value: string) => {
        const maxLength = 100;
        if (value && value.length > maxLength) {
          return (
            <Tooltip title={value} placement="top">
              <Typography variant="body2" sx={{ cursor: "pointer" }}>
                {value.substring(0, maxLength)}...
              </Typography>
            </Tooltip>
          );
        }
        return <Typography variant="body2">{value || t("review_moderation.no_text")}</Typography>;
      },
    },
    {
      id: "review_date",
      label: t("review_moderation.col.date"),
      minWidth: 140,
      align: "center",
      format: (value: string) => {
        return <div>{moment(value).format("YYYY-MM-DD HH:mm")}</div>;
      },
    },
    {
      id: "action",
      label: t("review_moderation.col.actions"),
      minWidth: 150,
      align: "center",
      format: (value: any, row: PendingReview) => {
        return (
          <Box display="flex" gap={1} justifyContent="center">
            <Tooltip title={t("review_moderation.approve_tooltip")}>
              <span>
                <IconButton
                  color="success"
                  onClick={() => handleApprove(row.id)}
                  disabled={isApprovingId === row.id}
                  size="small"
                >
                  {isApprovingId === row.id ? (
                    <CircularProgress size={20} />
                  ) : (
                    <CheckCircleIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t("review_moderation.reject_tooltip")}>
              <IconButton
                color="error"
                onClick={() => handleOpenRejectModal(row.id)}
                disabled={isApprovingId === row.id}
                size="small"
              >
                <CancelIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  return (
    <div style={{ width: "100%", padding: "20px" }}>
      <Card>
        <Box sx={{ padding: 3 }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold" }}>
            {t("review_moderation.title")}
          </Typography>

          {isLoading ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="300px"
            >
              <CircularProgress />
            </Box>
          ) : (
            <MuiTable
              header={header}
              data={dataTable}
              page={page}
              rowsPerPage={rowsPerPage}
              totalElements={totalElements}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Box>
      </Card>

      {/* Reject Modal */}
      <Dialog
        open={openRejectModal}
        onClose={handleCloseRejectModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("review_moderation.reject_title")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t("review_moderation.reject_desc")}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label={t("review_moderation.reason_label")}
            type="text"
            fullWidth
            multiline
            rows={4}
            value={rejectionNote}
            onChange={(e) => setRejectionNote(e.target.value)}
            placeholder={t("review_moderation.reason_placeholder")}
            required
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseRejectModal}
            disabled={isSubmittingReject}
            color="inherit"
          >
            {t("btn.cancel")}
          </Button>
          <Button
            onClick={handleRejectSubmit}
            disabled={isSubmittingReject || !rejectionNote.trim()}
            variant="contained"
            color="error"
          >
            {isSubmittingReject ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                {t("review_moderation.rejecting")}
              </>
            ) : (
              t("review_moderation.reject_title")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ReviewModeration;
